import React, { useEffect, useState } from 'react'

import GradientButton from '../../components/GradientButton'
import { useT } from '../../i18n'
import { DEFAULT_MORTALITY_REFERENCE, readHatcheryDefaults, type HatcheryDefaults, type MortalityReferenceKind } from '../../utils/hatchery'

// Hatchery v0 (findings #84): facility defaults for egg lots — the TUA at
// which eggs hatch and alevins start feeding (each lot can override them),
// and the fry-to-egg weight factor behind the estimated first-feeding
// weight. Kept in `Farm.metadata.hatcheryDefaults` (no migration).
// Findings #194: also the daily mortality reference (green up to / red from)
// for egg days, yolk-sac days and the critical days of early stages; empty
// fields use the suggested values, shown as placeholders.

const MORTALITY_ROWS: Array<{ kind: MortalityReferenceKind; green: keyof HatcheryDefaults; red: keyof HatcheryDefaults }> = [
  { kind: 'eggs', green: 'eggMortalityGreenPct', red: 'eggMortalityRedPct' },
  { kind: 'yolkSac', green: 'yolkSacMortalityGreenPct', red: 'yolkSacMortalityRedPct' },
  { kind: 'critical', green: 'criticalMortalityGreenPct', red: 'criticalMortalityRedPct' }
]
const MORTALITY_KEYS = MORTALITY_ROWS.flatMap((row) => [row.green, row.red])

const showSaved = (defaults: HatcheryDefaults): Record<string, string> => Object.fromEntries(
  MORTALITY_KEYS.map((key) => [key, show((defaults[key] as number | null | undefined) ?? null)])
)

export type FacilityHatcherySettingsProps = {
  farm: { id: string; metadata?: Record<string, unknown> }
  disabled: boolean
  onSave: (defaults: HatcheryDefaults) => Promise<void>
}

const parse = (value: string): number | null | 'invalid' => {
  const trimmed = value.trim().replace(',', '.')
  if (!trimmed) return null
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 'invalid'
}

const show = (value: number | null): string => (value === null ? '' : String(value))

export default function FacilityHatcherySettings({ farm, disabled, onSave }: FacilityHatcherySettingsProps) {
  const t = useT()
  const saved = readHatcheryDefaults(farm)
  const [hatch, setHatch] = useState(show(saved.hatchTargetTua))
  const [firstFeeding, setFirstFeeding] = useState(show(saved.firstFeedingTargetTua))
  const [factor, setFactor] = useState(show(saved.fryWeightFactor))
  const savedMortality = showSaved(saved)
  const savedMortalityKey = JSON.stringify(savedMortality)
  const [mortality, setMortality] = useState<Record<string, string>>(savedMortality)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setHatch(show(saved.hatchTargetTua))
    setFirstFeeding(show(saved.firstFeedingTargetTua))
    setFactor(show(saved.fryWeightFactor))
    setMortality(JSON.parse(savedMortalityKey) as Record<string, string>)
    setError(null)
  }, [farm.id, saved.hatchTargetTua, saved.firstFeedingTargetTua, saved.fryWeightFactor, savedMortalityKey])

  const dirty = hatch.trim() !== show(saved.hatchTargetTua)
    || firstFeeding.trim() !== show(saved.firstFeedingTargetTua)
    || factor.trim() !== show(saved.fryWeightFactor)
    || MORTALITY_KEYS.some((key) => (mortality[key] ?? '').trim() !== savedMortality[key])

  const save = async () => {
    const next = { hatchTargetTua: parse(hatch), firstFeedingTargetTua: parse(firstFeeding), fryWeightFactor: parse(factor) }
    if (Object.values(next).some((value) => value === 'invalid')
      || (typeof next.fryWeightFactor === 'number' && next.fryWeightFactor > 5)) {
      setError(t('editFarm.hatchery.invalid'))
      return
    }
    if (typeof next.hatchTargetTua === 'number' && typeof next.firstFeedingTargetTua === 'number'
      && next.firstFeedingTargetTua <= next.hatchTargetTua) {
      setError(t('lots.hatchery.error.targetsOrder'))
      return
    }
    const nextMortality: Record<string, number | null> = {}
    for (const row of MORTALITY_ROWS) {
      const green = parse(mortality[row.green] ?? '')
      const red = parse(mortality[row.red] ?? '')
      const greenValue = green === null ? DEFAULT_MORTALITY_REFERENCE[row.kind].greenMaxPct : green
      const redValue = red === null ? DEFAULT_MORTALITY_REFERENCE[row.kind].redFromPct : red
      if (greenValue === 'invalid' || redValue === 'invalid' || greenValue > 100 || redValue > 100 || redValue <= greenValue) {
        setError(t('editFarm.hatchery.mortality.invalid'))
        return
      }
      nextMortality[row.green] = green as number | null
      nextMortality[row.red] = red as number | null
    }
    setError(null)
    setSaving(true)
    try {
      await onSave({ ...next, ...nextMortality } as HatcheryDefaults)
    } finally {
      setSaving(false)
    }
  }

  const inputStyle: React.CSSProperties = { padding: '8px 10px', border: '1px solid #cbd5f5', borderRadius: 10, width: 120 }

  return (
    <div data-testid="edit-farm-hatchery-settings" style={{ marginTop: 12, display: 'grid', gap: 8 }}>
      <strong>{t('editFarm.hatchery.title')}</strong>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label style={{ display: 'grid', gap: 2, fontSize: 13 }}>
          <span>{t('editFarm.hatchery.hatchTargetTua')}</span>
          <input data-testid="edit-farm-hatchery-hatch" inputMode="decimal" value={hatch} onChange={(event) => setHatch(event.target.value)} disabled={disabled} style={inputStyle} />
        </label>
        <label style={{ display: 'grid', gap: 2, fontSize: 13 }}>
          <span>{t('editFarm.hatchery.firstFeedingTargetTua')}</span>
          <input data-testid="edit-farm-hatchery-first-feeding" inputMode="decimal" value={firstFeeding} onChange={(event) => setFirstFeeding(event.target.value)} disabled={disabled} style={inputStyle} />
        </label>
        <label style={{ display: 'grid', gap: 2, fontSize: 13 }}>
          <span>{t('editFarm.hatchery.fryWeightFactor')}</span>
          <input data-testid="edit-farm-hatchery-factor" inputMode="decimal" value={factor} placeholder="1" onChange={(event) => setFactor(event.target.value)} disabled={disabled} style={inputStyle} />
        </label>
        <GradientButton size="xs" variant="success" onClick={() => void save()} disabled={disabled || saving || !dirty} data-testid="edit-farm-hatchery-save">
          {t('common.save')}
        </GradientButton>
      </div>
      <span style={{ fontSize: 12, color: '#6b7280' }}>{t('editFarm.hatchery.help')}</span>
      <div data-testid="edit-farm-hatchery-mortality" style={{ display: 'grid', gridTemplateColumns: 'max-content 90px 90px', gap: '6px 12px', alignItems: 'center', fontSize: 13, marginTop: 4 }}>
        <strong style={{ gridColumn: '1 / -1' }}>{t('editFarm.hatchery.mortality.title')}</strong>
        <span />
        <span style={{ color: '#16a34a' }}>{t('editFarm.hatchery.mortality.green')}</span>
        <span style={{ color: '#dc2626' }}>{t('editFarm.hatchery.mortality.red')}</span>
        {MORTALITY_ROWS.map((row) => (
          <React.Fragment key={row.kind}>
            <span>{t(`editFarm.hatchery.mortality.${row.kind}`)}</span>
            {([['green', row.green, DEFAULT_MORTALITY_REFERENCE[row.kind].greenMaxPct], ['red', row.red, DEFAULT_MORTALITY_REFERENCE[row.kind].redFromPct]] as const).map(([side, key, suggested]) => (
              <input
                key={side}
                data-testid={`edit-farm-hatchery-mortality-${row.kind}-${side}`}
                inputMode="decimal"
                value={mortality[key] ?? ''}
                placeholder={String(suggested)}
                onChange={(event) => setMortality((prev) => ({ ...prev, [key]: event.target.value }))}
                disabled={disabled}
                style={{ ...inputStyle, width: 80 }}
              />
            ))}
          </React.Fragment>
        ))}
      </div>
      <span style={{ fontSize: 12, color: '#6b7280' }}>{t('editFarm.hatchery.mortality.help')}</span>
      {error ? <div role="alert" style={{ color: '#b91c1c', fontWeight: 700, fontSize: 13 }}>{error}</div> : null}
    </div>
  )
}
